import React, { useState } from 'react';
import { CatalogApiObject } from '@europrocurement/l2d-domain';
import { Box, Typography } from '@mui/material';
import {
    BoxSkeleton,
    ContinueButton,
    MosaicSkeleton,
    SearchBar,
    SkeletonLoader,
} from '@europrocurement/flexy-components';
import useSelectFormality from '../hooks/useSelectFormality';
import SelectFormalityChoices from './SelectFormalityChoices';
import SelectFormalitySearchResults from './SelectFormalitySearchResults';
import SearchChips from './SearchChips';

export type SelectFormalityProps = {
    defaultGroups?: Array<number>;
    defaultCatalogs?: Array<number>;
    onChange?: (catalogs: CatalogApiObject[]) => void;
    prescripteurId: number;
    origine: number;
    domaine: number;
    nomCommercial?: string;
    disableSubmit?: boolean;
};

const SelectFormality: React.FunctionComponent<SelectFormalityProps> = function (props) {
    const {
        onChange,
        prescripteurId,
        origine,
        domaine,
        nomCommercial,
        defaultGroups = [],
        defaultCatalogs = [],
        disableSubmit = false,
    } = props;
    const [searchTerm, setSearchTerm] = useState<string>(nomCommercial || '');
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const {
        catalogs,
        groups,
        requestState,
        getFilteredCatalogs,
        checkGroup,
        uncheckGroup,
        checkCatalog,
        uncheckCatalog,
    } = useSelectFormality(prescripteurId, origine, domaine, defaultGroups, defaultCatalogs);
    const checkedCatalogs = catalogs.filter((catalog) => catalog.checked);
    console.log(groups);

    if (requestState.error) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '25px' }}>
                <Typography variant="l2dbodys">Une erreur est survenue</Typography>
            </Box>
        );
    }

    const handleCheckCatalog = (catalogId: number) => {
        checkCatalog(catalogId);
        setSearchTerm('');
        setIsSearching(false);
    };

    const handleUncheckCatalog = (catalogId: number) => {
        uncheckCatalog(catalogId);
        setSearchTerm('');
        setIsSearching(false);
    };

    return (
        <>
            <SkeletonLoader
                isLoading={!requestState.isLoaded || requestState.isProcessing}
                skeleton={
                    <BoxSkeleton
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'start',
                            width: '100%',
                            height: '56px',
                            marginTop: '40px',
                        }}
                    />
                }
            >
                <SearchChips
                    catalogs={catalogs.filter((catalog) => catalog.checked)}
                    uncheckCatalog={uncheckCatalog}
                />
                <SearchBar
                    className="search"
                    placeholder="Rechercher une formalité"
                    value={searchTerm}
                    onDelete={() => {
                        setSearchTerm('');
                        setIsSearching(false);
                    }}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const searchValue = event.target.value || '';
                        setSearchTerm(searchValue);
                        setIsSearching(searchValue !== '');
                    }}
                />
            </SkeletonLoader>
            {isSearching ? (
                <SelectFormalitySearchResults
                    catalogs={getFilteredCatalogs(searchTerm)}
                    onCheckCatalog={handleCheckCatalog}
                    onUncheckCatalog={handleUncheckCatalog}
                />
            ) : (
                <SkeletonLoader
                    isLoading={!requestState.isLoaded || requestState.isProcessing}
                    skeleton={
                        <MosaicSkeleton
                            columns={4}
                            rows={2}
                            sx={{
                                paddingTop: '30px',
                            }}
                            sxItem={{
                                height: '153px',
                            }}
                        />
                    }
                >
                    <SelectFormalityChoices
                        catalogs={getFilteredCatalogs(searchTerm)}
                        groups={groups}
                        onCheckGroup={checkGroup}
                        onUncheckGroup={uncheckGroup}
                        onCheckCatalog={checkCatalog}
                        onUncheckCatalog={uncheckCatalog}
                    />
                </SkeletonLoader>
            )}
            {checkedCatalogs.length > 0 && (
                <ContinueButton
                    disabled={disableSubmit}
                    onClick={() => {
                        if (onChange) {
                            onChange(checkedCatalogs.map((catalog) => catalog.apiObject));
                        }
                    }}
                />
            )}
        </>
    );
};

export default SelectFormality;
